import React, { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import i18next from '@/translation/i18n';
import { Center } from '@mantine/core';
import { errorReportingService } from '../../../shared/src/services/errorReportingService';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    isSessionExpired: boolean;
    isDynamicImportError: boolean;
}

const DYNAMIC_IMPORT_RELOAD_KEY = 'dynamic-import-reload-at';
const DYNAMIC_IMPORT_RETRY_INTERVAL_MS = 60_000;
let isReloadScheduled = false;

const handleReload = () => {
    if (isReloadScheduled) return;
    isReloadScheduled = true;

    const now = Date.now();

    try {
        const lastReloadAt = Number(
            sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) ?? 0
        );

        if (now - lastReloadAt < DYNAMIC_IMPORT_RETRY_INTERVAL_MS) {
            return;
        }

        sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, String(now));
    } catch (error) {
        console.warn('동적 모듈 재시도 상태를 저장하지 못했습니다.', error);
        return;
    }

    window.location.reload();
};

// 동적 임포트 에러 감지를 위한 이벤트 리스너
const setupDynamicImportErrorHandler = () => {
    const handlePreloadError = (event: Event) => {
        event.preventDefault();
        console.warn('Vite preload 오류 감지. 페이지를 새로고침합니다...');
        handleReload();
    };

    const handleChunkLoadError = (event: ErrorEvent) => {
        const isChunkLoadError =
            event.message &&
            (event.message.includes('Loading chunk') ||
                event.message.includes(
                    'Failed to fetch dynamically imported module'
                ));

        if (isChunkLoadError) {
            console.warn(
                '동적으로 로드된 모듈에 오류가 감지되었습니다. 페이지를 새로고침합니다...'
            );
            handleReload();
        }
    };

    window.addEventListener('vite:preloadError', handlePreloadError);
    window.addEventListener('error', handleChunkLoadError);

    return () => {
        window.removeEventListener('vite:preloadError', handlePreloadError);
        window.removeEventListener('error', handleChunkLoadError);
    };
};

// 컴포넌트가 마운트될 때 이벤트 리스너 설정
const DynamicImportErrorHandler: React.FC = () => {
    useEffect(() => {
        return setupDynamicImportErrorHandler();
    }, []);

    return null;
};

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        isSessionExpired: false,
        isDynamicImportError: false,
    };

    public static getDerivedStateFromError(error: Error): State {
        // 동적 임포트 에러 확인
        const isDynamicImportError =
            error.message &&
            (error.message.includes('Loading chunk') ||
                error.message.includes(
                    'Failed to fetch dynamically imported module'
                ));

        // 동적 임포트 에러인 경우
        if (isDynamicImportError) {
            return {
                hasError: true,
                isSessionExpired: false,
                isDynamicImportError: true,
            };
        }

        // 세션 만료 오류 또는 404 오류인지 확인
        if (
            error.message.includes('세션이 만료되었습니다') ||
            error.message.includes('404') ||
            error.message.includes('Not Found')
        ) {
            return {
                hasError: true,
                isSessionExpired: true,
                isDynamicImportError: false,
            };
        }

        return {
            hasError: true,
            isSessionExpired: false,
            isDynamicImportError: false,
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('오류 발생:', error, errorInfo);
        const { user } = useAuthStore.getState();

        // 동적 임포트 에러는 배포로 청크 파일명이 바뀌어 발생하며, 아래에서
        // 자동 새로고침으로 복구되므로 조치할 것이 없다. error_logs 적재를 스킵해
        // 배포성 노이즈를 남기지 않는다.
        if (!this.state.isDynamicImportError) {
            void errorReportingService.reportError(
                user?.id,
                null,
                error,
                'main-app-react-boundary',
                {
                    componentStack: errorInfo.componentStack,
                    isSessionExpired: this.state.isSessionExpired,
                },
                'error'
            );
        }

        try {
            // 동적 임포트 에러 확인
            const isDynamicImportError =
                error.message &&
                (error.message.includes('Loading chunk') ||
                    error.message.includes(
                        'Failed to fetch dynamically imported module'
                    ));

            // 동적 임포트 에러인 경우 페이지 새로고침
            if (isDynamicImportError) {
                console.warn(
                    '동적으로 로드된 모듈에 오류가 감지되었습니다. 페이지를 새로고침합니다...'
                );
                handleReload();
                return;
            }

            // 세션 만료 오류 또는 404 오류인지 확인
            if (
                error.message.includes('세션이 만료되었습니다') ||
                error.message.includes('404') ||
                error.message.includes('Not Found')
            ) {
                // 안전하게 로그아웃 처리
                useAuthStore.getState().logout();
                this.setState({ isSessionExpired: true });
            }
        } catch (err) {
            console.error('오류 처리 중 추가 오류 발생:', err);
        }
    }

    public render() {
        // i18next가 초기화되었는지 확인
        const t = i18next.isInitialized ? i18next.t : (key: string) => key;

        if (this.state.hasError) {
            if (this.state.isDynamicImportError) {
                // 동적 임포트 에러는 componentDidCatch에서 이미 처리 중이므로
                // 여기서는 잠시 로딩 메시지만 표시
                return (
                    <Center h="100vh" ta="center">
                        <h2>
                            {t('common:error.module.loading')}
                            <br />
                            {t('common:error.module.refresh')}
                        </h2>
                    </Center>
                );
            }

            if (this.state.isSessionExpired) {
                return <Navigate to="/auth/login" replace />;
            }

            // 다른 오류에 대한 처리
            return (
                <Center h="100vh" ta="center">
                    <h1>{t('common:error.refresh')}</h1>
                </Center>
            );
        }

        return (
            <>
                <DynamicImportErrorHandler />
                {this.props.children}
            </>
        );
    }
}

export default ErrorBoundary;
